import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { aufgabenQuery, sitzungQuery, type Aufgabe } from '../../lib/queries'

/**
 * **Meine Aufgaben über alle Arbeitswelten** – die eine Stelle, an der die beiden neuen
 * Ansichten von „Mein Tag" den Bestand lesen
 * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md)).
 *
 * ## Warum dieser Umweg und nicht je Ansicht `aufgabenQuery`
 *
 * Weil `welt.trennung.test.ts` das verbietet – und der Wächter hat recht. Wer den Gesamtbestand
 * liest, muss sich erklären, und die Ausnahmeliste ist **gedeckelt**: *„Eine Ausnahmeliste ohne
 * Gründe wächst, bis sie alles enthält – und dann prüft der Test nichts mehr."*
 *
 * Circle und Kalender brauchen beide dasselbe: **was mir gehört, quer über meine Welten.** Das
 * zweimal aufzuschreiben wären zwei Ausnahmen für eine Frage – und zwei Stellen, die
 * auseinanderlaufen, sobald jemand „gehört mir" anders fasst.
 *
 * ## Die Regel für „gehört mir"
 *
 * `work_assignee_id` schlägt `assignee_id`, wie überall
 * ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)): Was GitLab oder NetCon
 * als Bearbeiter führen, sagt über Work nichts. Erst wenn Work niemanden kennt, gilt die Quelle
 * als Hinweis.
 *
 * ## Warum über alle Welten
 *
 * Dieselbe Begründung wie bei `lage.ts` ([E-234](../../../../../docs/ENTSCHEIDUNGEN.md)): **Ein
 * Mensch hat einen Tag**, auch wenn seine Arbeit aus drei Arbeitswelten kommt. Ein Tagesplan,
 * der die Hälfte der laufenden Aufgaben verschweigt, wäre schlimmer als keiner – und ein Rad mit
 * einem Keil ist kein Rad.
 */
export function useMeineAufgaben(): { aufgaben: Aufgabe[]; ichId: string | null } {
  const { data: alle = [] } = useQuery(aufgabenQuery)
  const { data: sitzung } = useQuery(sitzungQuery)
  const ichId = sitzung?.id ?? null

  const aufgaben = useMemo(
    () => (ichId ? alle.filter((a) => (a.workAssigneeId ?? a.assigneeId) === ichId) : []),
    [alle, ichId],
  )

  return { aufgaben, ichId }
}
